import * as React from 'react'

export const LIVE_CLOCK_DRIFT_TOLERANCE_MS = 2500

export const resolveSmoothLiveNow = (localNow, liveNow, toleranceMs = LIVE_CLOCK_DRIFT_TOLERANCE_MS) => {
    const numericLocalNow = Number(localNow)
    const numericLiveNow = Number(liveNow)

    if (!Number.isFinite(numericLocalNow))
        return Number.isFinite(numericLiveNow) ? numericLiveNow : Date.now()

    if (!Number.isFinite(numericLiveNow))
        return numericLocalNow

    return numericLiveNow - numericLocalNow > toleranceMs ? numericLiveNow : numericLocalNow
}

const resolveAnchoredLiveNow = (localNow, anchor) => {
    const numericLocalNow = Number(localNow)
    const anchorLocalNow = Number(anchor?.localNow)
    const anchorLiveNow = Number(anchor?.liveNow)

    if (!Number.isFinite(numericLocalNow))
        return Number.isFinite(anchorLiveNow) ? anchorLiveNow : Date.now()

    if (!Number.isFinite(anchorLocalNow) || !Number.isFinite(anchorLiveNow))
        return numericLocalNow

    return anchorLiveNow + Math.max(0, numericLocalNow - anchorLocalNow)
}

export const useSmoothLiveNow = (liveNow, toleranceMs = LIVE_CLOCK_DRIFT_TOLERANCE_MS) => {
    const [localNow, setLocalNow] = React.useState(() => Date.now())
    const [anchor, setAnchor] = React.useState(null)

    React.useEffect(() => {
        if (typeof window === 'undefined')
            return undefined

        const syncNow = () => setLocalNow(Date.now())
        const timer = window.setInterval(syncNow, 1000)
        window.addEventListener('focus', syncNow)
        document.addEventListener('visibilitychange', syncNow)

        return () => {
            window.clearInterval(timer)
            window.removeEventListener('focus', syncNow)
            document.removeEventListener('visibilitychange', syncNow)
        }
    }, [])

    React.useEffect(() => {
        const numericLiveNow = Number(liveNow)
        if (!Number.isFinite(numericLiveNow))
            return

        const currentLocalNow = Date.now()
        setLocalNow(currentLocalNow)
        setAnchor(currentAnchor => {
            const anchoredNow = currentAnchor ? resolveAnchoredLiveNow(currentLocalNow, currentAnchor) : currentLocalNow
            if (numericLiveNow - anchoredNow > toleranceMs) {
                return {
                    localNow: currentLocalNow,
                    liveNow: numericLiveNow
                }
            }

            return currentAnchor
        })
    }, [liveNow, toleranceMs])

    return React.useMemo(() => {
        const anchoredNow = resolveAnchoredLiveNow(localNow, anchor)
        const numericLiveNow = Number(liveNow)

        if (!Number.isFinite(numericLiveNow))
            return anchoredNow

        return Math.max(anchoredNow, numericLiveNow)
    }, [anchor, liveNow, localNow])
}
